import React from 'react';
import { Link } from 'react-router-dom';
import { RESULT_TYPES, COLORS } from '../constants/config';
import { isFeatureEnabled } from '../constants/featureFlags';
import ContactLink from './ContactLink';

const ResultPage = ({ type, title, message, bookingDetails, actions, content }) => {
  const isSuccess = type === RESULT_TYPES.success;
  const isError = type === RESULT_TYPES.error;
  const isNotFound = type === RESULT_TYPES.notFound;

  const getIcon = () => {
    if (isSuccess) return '✓';
    if (isError) return '✗';
    if (isNotFound) return '404';
    return '!';
  };

  const getIconColor = () => {
    if (isSuccess) return COLORS.success;
    if (isError) return COLORS.error;
    return COLORS.primary;
  };

  return (
    <div className="result-page">
      <div className="container">
        <div className="result-content">
          <div className="result-icon" style={{ color: getIconColor() }}>
            {getIcon()}
          </div>

          <h1>{title}</h1>
          <p className="result-message">{message}</p>

          {isSuccess && bookingDetails && (
            <div className="booking-summary">
              <h2>Your Booking Details</h2>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Name:</strong>
                  <p>{bookingDetails.name}</p>
                </div>
                <div className="summary-item">
                  <strong>Email:</strong>
                  <p>{bookingDetails.email}</p>
                </div>
                <div className="summary-item">
                  <strong>Date:</strong>
                  <p>{bookingDetails.date || 'Not specified'}</p>
                </div>
                <div className="summary-item">
                  <strong>Passengers:</strong>
                  <p>{bookingDetails.passengers || 'Not specified'}</p>
                </div>
              </div>
            </div>
          )}

          {isSuccess && (
            <div className="next-steps">
              <h2>What Happens Next?</h2>
              <ul>
                <li>We'll send you a confirmation email shortly</li>
                <li>Our team will contact you within 24 hours to confirm details</li>
                <li>We'll discuss your tour preferences and any special requirements</li>
                <li>Finalize payment and booking</li>
              </ul>
            </div>
          )}

          {isError && (
            <div className="error-solutions">
              <h2>What You Can Do</h2>
              <div className="solutions-grid">
                <div className="solution-item">
                  <h3>📞 Call Us</h3>
                  <p>Get in touch directly with our team for immediate assistance</p>
                  <ContactLink type="phone" label="Call Now" className="btn btn-primary button-spacing" />
                </div>
                <div className="solution-item">
                  <h3>📧 Email Us</h3>
                  <p>Send us a detailed message and we'll respond promptly</p>
                  <ContactLink type="email" label="Email Now" className="btn btn-primary button-spacing" />
                </div>
                <div className="solution-item">
                  <h3>📋 Try Again</h3>
                  <p>Return to the form and resubmit your enquiry</p>
                  <Link to="/contact" className="btn btn-primary button-spacing">
                    Back to Form
                  </Link>
                </div>
              </div>
            </div>
          )}

          {isError && (
            <div className="troubleshooting">
              <h2>Troubleshooting Tips</h2>
              <ul>
                <li>Check your internet connection</li>
                <li>Verify all required fields are filled correctly</li>
                <li>Try again in a few moments</li>
                <li>Contact us directly if the problem persists</li>
              </ul>
            </div>
          )}

          {isNotFound && (
            <div className="not-found-suggestions">
              <h3>Suggested Pages</h3>
              <div className="suggestions-grid">
                {isFeatureEnabled('tours') && (
                  <Link to="/tours" className="suggestion-card">
                    <h4>Tours</h4>
                    <p>Explore our tour packages</p>
                  </Link>
                )}
                {isFeatureEnabled('about') && (
                  <Link to="/about" className="suggestion-card">
                    <h4>About</h4>
                    <p>Learn more about Travel Mhor</p>
                  </Link>
                )}
                {isFeatureEnabled('faq') && (
                  <Link to="/faq" className="suggestion-card">
                    <h4>FAQ</h4>
                    <p>Find answers to common questions</p>
                  </Link>
                )}
                {isFeatureEnabled('contact') && (
                  <Link to="/contact" className="suggestion-card">
                    <h4>Contact</h4>
                    <p>Get in touch with our team</p>
                  </Link>
                )}
              </div>
            </div>
          )}

          {content && <div>{content}</div>}

          <div className="result-actions">
            <Link to="/" className="btn btn-primary">
              Return Home
            </Link>
            {isFeatureEnabled('tours') && (
              <Link to="/tours" className="btn btn-secondary">
                View Tours
              </Link>
            )}
            {isError && (
              <Link to="/contact" className="btn btn-primary">
                Try Again
              </Link>
            )}
          </div>

          <div className="contact-reminder">
            <p>
              <strong>Need help?</strong> Contact us at{' '}
              <ContactLink type="email" className="link-primary" />{' '}
              or call{' '}
              <ContactLink type="phone" className="link-primary" />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;
